<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col>
        <hm-spinner v-if="!ready" />
        <b-form @submit.prevent="onSubmit">
          <b-card header="Maintenance Logbook Entry (as needed)" v-show="ready">
            <Loading :active="busy" :is-full-page="false">
              <slot>
                <hm-spinner />
              </slot>
            </Loading>
            <hm-alert />
            <p>Keep a maintenance record (a.k.a. logbook) that includes a history of physical changes, upgrades, repairs, and other modifications to the facilities and the rooms where information systems and ePHI are kept.</p>
            <p>
              Consider how the office can evolve over time. For example, repurposing a file room for computer network servers might require you to address temperature and humidity controls, backup electrical service, surge protectors, air filtration, fire suppression
              systems, and door locks. The most frequent physical security changes may be rekeying door locks or changing the combination on a door when someone from the workforce has been terminated.
            </p>
            <hm-note>
              <u>Note</u>: You DO NOT need to log replacing a lightbulb, painting a wall, and other physical changes which have no impact on information systems whatsoever.
            </hm-note>
            <b-form-group>
              <div class="font-weight-bold" slot="label">
                Create a logbook entry
                <u>only when</u> a physical change, upgrade, repair, or modification is made to the practice.
              </div>
              <b-input-group>
                <b-form-textarea
                  no-resize
                  :rows="5"
                  name="txtLog"
                  class="form-control"
                  placeholder="Enter what changed, the reason for repair or modification, and who authorized the change"
                  v-model="log"
                  v-validate="'required'"
                  :disabled="busy"
                  :class="{ 'is-invalid': errors.has('txtLog') && submitted }"
                />
                <b-form-invalid-feedback id="txtLogFeedback">Please enter the log book entry details</b-form-invalid-feedback>
              </b-input-group>
            </b-form-group>
            <div slot="footer">
              <b-row>
                <b-col class="text-right">
                  <b-button type="submit" variant="primary" class="px-4" :disabled="busy">Save Logbook Entry</b-button>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </b-form>

        <b-card v-show="ready">
          <Loading :active="busy" :is-full-page="false">
            <slot>
              <hm-spinner />
            </slot>
          </Loading>
          <div slot="header" class="mb-0">
            Logbook Entries
            <b-button type="button" variant="secondary" size="sm" class="px-4 pull-right" :disabled="busy" v-show="!logEntriesLoaded" @click="onViewLogs">View</b-button>
          </div>
          <b-table class="p-1" responsive bordered show-empty empty-text="No Log Entries" v-show="logEntriesLoaded" :items="logEntries" :fields="columns" :sort-by.sync="sortBy" :sort-desc="true"></b-table>
          <div class="text-center" v-show="!logEntriesLoaded">Click View button to show current log entries</div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { practiceService } from '@/services'
import { formatDate } from '@/filters/days'

export default {
  name: 'Practice.Logbook',
  data() {
    return {
      log: '',
      logEntries: [],
      sortBy: 'createdAt',
      columns: [{
        key: 'createdAt',
        label: 'Date',
        variant: 'secondary',
        sortable: true,
        thStyle: { 'min-width': '150px !important' },
        formatter: (value) => { return formatDate(value); }
      }, {
        key: 'log',
        label: 'Details'
      }],
      ready: true,
      busy: false,
      submitted: false,
      logEntriesLoaded: false
    }
  },
  methods: {
    ...mapActions({
      alertClear: 'alert/clear',
      alertSuccess: 'alert/success',
      alertError: 'alert/error',
      alertException: 'alert/exception'
    }),
    onSubmit() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      this.$validator.validate()
        .then(valid => {
          if (valid) {
            return practiceService.setLogbook(this.log)
              .then(data => {
                this.log = '';
                this.logEntries = [];
                this.logEntriesLoaded = false;
                this.alertSuccess('Log entry added successfully!');
              })
              .catch(err => {
                this.alertException({ err, message: 'Error saving log entry.' });
              });
          } else {
            this.alertError('Please complete log entry and submit again.');
          }
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onViewLogs() {
      this.busy = true;
      this.alertClear();

      return practiceService.getLogbook()
        .then(data => {
          this.logEntries = data;
          this.logEntriesLoaded = true;
        })
        .catch(err => {
          this.alertException({ err, message: 'Error loading log entries.' });
        })
        .finally(() => {
          this.busy = false;
        });
    }
  }
}
</script>